<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <!-- Header -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col class="text-center">
        <h4 class="mb-0">X.com — источники</h4>
      </v-col>
      <v-col cols="auto">
        <div class="d-flex align-center ga-2">
          <v-text-field
            v-model="filters.username"
            placeholder="@handle или имя"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            style="width: 260px;"
            @update:model-value="onFilterDebounce"
          ></v-text-field>
          <v-select
            v-model="filters.is_monitoring"
            :items="monitoringOptions"
            item-title="text"
            item-value="value"
            placeholder="Мониторинг"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            style="width: 160px;"
            @update:model-value="loadSources"
          ></v-select>
          <span class="text-caption font-weight-bold">Найдено: {{ total }}</span>
        </div>
      </v-col>
    </v-row>

    <v-card>
      <v-card-text class="pa-0">
        <v-data-table-server
          v-model="selected"
          :headers="headers"
          :items="sources"
          :items-length="total"
          :loading="isLoading"
          :items-per-page="itemsPerPage"
          :page="page"
          item-value="id"
          show-select
          class="elevation-1"
          hover
          fixed-header
          height="calc(100vh - 236px)"
          :items-per-page-options="[20, 50, 100]"
          @update:options="onTableOptions"
        >
          <template v-slot:[`item.logo`]="{ item }">
            <v-avatar size="40">
              <v-img v-if="item.ext_thumbnail_url" :src="item.ext_thumbnail_url" cover></v-img>
              <v-icon v-else color="grey-lighten-1">mdi-account</v-icon>
            </v-avatar>
          </template>

          <template v-slot:[`item.handle`]="{ item }">
            <a :href="`https://x.com/${handleOf(item)}`" target="_blank" rel="noopener" class="text-primary text-decoration-none">
              @{{ handleOf(item) }}
            </a>
          </template>

          <template v-slot:[`item.display_name`]="{ item }">
            {{ item.display_name || '' }}
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip :color="statusColor(item.status)" size="small" variant="tonal">
              {{ statusLabel(item.status) }}
            </v-chip>
          </template>

          <template v-slot:[`item.followers`]="{ item }">
            <div class="text-right">{{ formatNumber(item.followers) }}</div>
          </template>

          <template v-slot:[`item.updated`]="{ item }">
            {{ formatDate(item.updated) }}
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:[`item.messages_count`]="{ item }">
            <div
              class="text-right"
              :class="{ 'text-primary cursor-pointer': getMessagesCount(item) > 0 }"
              @click="getMessagesCount(item) > 0 && openMessagesModal(item)"
            >
              {{ formatNumber(getMessagesCount(item)) }}
            </div>
          </template>

          <template v-slot:[`item.primary_lang`]="{ item }">
            <span v-if="item.primary_lang">{{ item.primary_lang.toUpperCase() }}</span>
          </template>

          <template v-slot:[`item.description`]="{ item }">
            <v-tooltip v-if="item.description" :text="item.description" location="top" max-width="480">
              <template v-slot:activator="{ props }">
                <span v-bind="props">{{ truncate(item.description) }}</span>
              </template>
            </v-tooltip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex justify-center">
              <v-tooltip v-if="item.is_monitoring" text="Остановить">
                <template v-slot:activator="{ props }">
                  <v-btn v-bind="props" icon="mdi-stop" size="x-small" variant="text" color="warning" @click="stopMonitoring(item)"></v-btn>
                </template>
              </v-tooltip>
              <v-tooltip v-else text="Запустить">
                <template v-slot:activator="{ props }">
                  <v-btn v-bind="props" icon="mdi-play" size="x-small" variant="text" color="success" :disabled="item.is_processing" @click="startMonitoring(item)"></v-btn>
                </template>
              </v-tooltip>
              <v-tooltip text="Обновить информацию">
                <template v-slot:activator="{ props }">
                  <v-btn v-bind="props" icon="mdi-information" size="x-small" variant="text" :loading="item.is_processing" :disabled="item.is_monitoring" @click="refreshInfo(item)"></v-btn>
                </template>
              </v-tooltip>
              <v-tooltip text="Редактировать">
                <template v-slot:activator="{ props }">
                  <v-btn v-bind="props" icon="mdi-pencil" size="x-small" variant="text" color="primary" :disabled="item.is_processing" @click="openEditModal(item)"></v-btn>
                </template>
              </v-tooltip>
              <v-tooltip text="Удалить">
                <template v-slot:activator="{ props }">
                  <v-btn v-bind="props" icon="mdi-delete" size="x-small" variant="text" color="error" :disabled="item.is_monitoring || item.is_processing" @click="confirmDelete(item)"></v-btn>
                </template>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="text-center pa-8">
              <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-alpha-x-circle-outline</v-icon>
              <div class="text-body-1 text-grey">Пока нет X-источников</div>
              <div class="text-caption text-grey">Они появляются автоматически после поисковых запросов или добавления «своих источников»</div>
            </div>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>

    <SourceContentModal
      v-model="messagesModal.visible"
      :source-id="messagesModal.sourceId"
      :title="messagesModal.title"
      items-label="сообщений"
    />

    <!-- Edit modal -->
    <v-dialog v-model="editModal.visible" max-width="600px" persistent @keydown.esc="closeEditModal">
      <v-card v-if="editModal.item">
        <v-card-title>
          <span class="text-h5">Редактировать источник</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" class="text-center">
                <v-avatar size="70">
                  <v-img v-if="editModal.item.ext_thumbnail_url" :src="editModal.item.ext_thumbnail_url" cover></v-img>
                  <v-icon v-else size="40" color="grey-lighten-1">mdi-account</v-icon>
                </v-avatar>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <div class="mb-2"><strong>Никнейм:</strong> @{{ handleOf(editModal.item) }}</div>
                <div class="mb-2"><strong>Название:</strong> {{ editModal.item.display_name || '—' }}</div>
                <div class="mb-2"><strong>Подписчики:</strong> {{ formatNumber(editModal.item.followers) }}</div>
                <div class="mb-2"><strong>Сообщений:</strong> {{ formatNumber(getMessagesCount(editModal.item)) }}</div>
                <div class="mb-2"><strong>Описание:</strong> {{ editModal.item.description || '—' }}</div>
              </v-col>
            </v-row>
            <v-divider class="my-4"></v-divider>
            <v-row dense>
              <!-- Раздел / Страна не имеют смысла для search_query — это
                   виртуальный источник, не аккаунт-в-стране. Скрываем. -->
              <v-col v-if="!isSearchType(editModal.item)" cols="12">
                <v-select
                  v-model="editForm.topic_id"
                  :items="sections"
                  item-title="option"
                  item-value="value"
                  label="Раздел"
                  variant="outlined"
                  density="compact"
                  clearable
                ></v-select>
              </v-col>
              <v-col v-if="!isSearchType(editModal.item)" cols="12">
                <v-select
                  v-model="editForm.geolocation_id"
                  :items="countries"
                  item-title="option"
                  item-value="value"
                  label="Страна"
                  variant="outlined"
                  density="compact"
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="editForm.status"
                  :items="statusOptions"
                  item-title="text"
                  item-value="value"
                  label="Статус"
                  variant="outlined"
                  density="compact"
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-autocomplete
                  v-model="editForm.tags"
                  :items="tagSuggestions"
                  item-title="name"
                  item-value="id"
                  label="Теги"
                  multiple
                  chips
                  closable-chips
                  variant="outlined"
                  density="compact"
                  return-object
                  clearable
                  ref="tagAutocomplete"
                  @update:search="searchTags"
                  @keydown.enter="handleTagEnter"
                  @update:model-value="onTagSelected"
                >
                  <template #chip="{ props, item }">
                    <v-chip
                      v-bind="props"
                      :text="getTagName(item)"
                      closable
                      size="small"
                      :color="item.raw.id ? 'primary' : 'success'"
                    />
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeEditModal">Отмена</v-btn>
          <v-btn color="primary" variant="elevated" :loading="editModal.saving" @click="saveEdit">Сохранить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation -->
    <v-dialog v-model="deleteModal.visible" max-width="460" @keydown.esc="deleteModal.visible = false">
      <v-card v-if="deleteModal.item">
        <v-card-title>Удаление источника</v-card-title>
        <v-card-text>
          <div class="mb-2">
            Удалить источник <strong>@{{ handleOf(deleteModal.item) }}</strong>?
          </div>
          <div class="text-caption text-medium-emphasis">
            Вместе с ним будут удалены все связанные твиты в Elasticsearch
            и все project findings по этому источнику. Действие необратимо.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteModal.visible = false">Отмена</v-btn>
          <v-btn color="error" variant="elevated" :loading="deleteModal.deleting" @click="doDelete">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">{{ snackbar.text }}</v-snackbar>
  </v-container>
</template>

<script>
import apiClient from '@/apiClient';
import apiDataClient from '@/apiDataClient';
import SourceContentModal from '@/components/SourceContentModal.vue';

// Sortable columns -> sort fields of GET /v2/platform-sources (core platform_source_repository
// allowed_sort_fields); a column missing here is not sortable
const X_SORT_FIELDS = {
  display_name: 'display_name',
  status: 'status',
  followers: 'subscribers',
  updated: 'updated_at',
  created_at: 'created_at',
  // stored by data after it saves tweets (sources_platform.messages_count)
  messages_count: 'messages_count',
};

export default {
  name: 'AdminX',
  components: { SourceContentModal },
  data() {
    return {
      sources: [],
      selected: [],
      total: 0,
      isLoading: false,
      page: 1,
      itemsPerPage: 50,
      filters: { username: '', is_monitoring: null },
      filterTimeout: null,
      monitoringOptions: [
        { text: 'Все', value: null },
        { text: 'Только активные', value: true },
        { text: 'Только выключенные', value: false },
      ],
      headers: [
        { title: '', key: 'logo', sortable: false, width: '80px' },
        { title: 'Никнейм', key: 'handle', sortable: false, width: '160px' },
        { title: 'Название', key: 'display_name', width: '200px' },
        { title: 'Статус', key: 'status', width: '110px' },
        { title: 'Подписчики', key: 'followers', align: 'end', width: '120px' },
        { title: 'Обновлено', key: 'updated', width: '130px' },
        { title: 'Создано', key: 'created_at', width: '130px' },
        { title: 'Сообщений', key: 'messages_count', align: 'end', width: '110px' },
        { title: 'Язык', key: 'primary_lang', sortable: false, width: '70px' },
        { title: 'Описание', key: 'description', sortable: false, width: '260px' },
        { title: 'Действия', key: 'actions', sortable: false, width: '140px' },
      ],
      sortBy: null,  // {key, order} of the table, sent as sort_by / sort_order
      messagesCount: {},  // source_id → count fetched from ES
      messagesModal: { visible: false, sourceId: null, title: '' },
      editModal: { visible: false, item: null, saving: false },
      editForm: { topic_id: null, geolocation_id: null, status: 'active', tags: [] },
      deleteModal: { visible: false, item: null, deleting: false },
      countries: [],
      sections: [],
      statusOptions: [
        { text: 'Активен', value: 'active' },
        { text: 'В ожидании', value: 'pending' },
        { text: 'Заблокирован', value: 'blocked' },
        { text: 'Удалён', value: 'deleted' },
      ],
      tagSuggestions: [],
      currentTagSearch: '',
      processingPollInterval: null,
      snackbar: { show: false, text: '', color: 'success' },
    };
  },
  beforeUnmount() {
    this.stopProcessingPoll();
  },
  mounted() {
    this.loadSources();
    this.loadReferenceData();
  },
  methods: {
    async loadSources() {
      this.isLoading = true;
      try {
        const params = {
          platform_id: 13,
          offset: (this.page - 1) * this.itemsPerPage,
          limit: this.itemsPerPage,
        };
        if (this.sortBy && X_SORT_FIELDS[this.sortBy.key]) {
          params.sort_by = X_SORT_FIELDS[this.sortBy.key];
          params.sort_order = this.sortBy.order === 'desc' ? 'desc' : 'asc';
        }
        if (this.filters.username) {
          params.username = this.filters.username;
        }
        if (this.filters.is_monitoring !== null && this.filters.is_monitoring !== undefined) {
          params.is_monitoring = this.filters.is_monitoring;
        }
        const resp = await apiClient.get('/v2/platform-sources', { params });
        this.sources = resp.data.items || [];
        this.total = resp.data.total || 0;
        this.fetchMessageCounts(this.sources);
        // If anything is mid-processing, keep polling until it settles.
        if (this.sources.some(s => s.is_processing)) {
          this.startProcessingPoll();
        }
      } catch (e) {
        console.error(e);
        this.showSnackbar('Ошибка загрузки источников', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    async fetchMessageCounts(items) {
      // PlatformSource.messages_count is not maintained for X (parser
      // pipeline doesn't increment it). Pull live counts from ES so the
      // 'Сообщений' column reflects real document totals.
      const ids = (items || []).map(s => s.id).filter(Boolean);
      if (!ids.length) return;
      try {
        const { data } = await apiDataClient.get('/source-content/count-by-sources', {
          params: { source_ids: ids.join(',') },
        });
        this.messagesCount = { ...this.messagesCount, ...(data.counts || {}) };
      } catch (e) {
        console.error('fetchMessageCounts error:', e);
      }
    },
    getMessagesCount(item) {
      if (!item || !item.id) return 0;
      return this.messagesCount[item.id] || 0;
    },
    openMessagesModal(item) {
      this.messagesModal.sourceId = item.id;
      this.messagesModal.title = item.display_name || `@${this.handleOf(item)}` || 'X.com';
      this.messagesModal.visible = true;
    },
    onTableOptions({ page, itemsPerPage, sortBy }) {
      let changed = false;
      if (page && page !== this.page) { this.page = page; changed = true; }
      if (itemsPerPage && itemsPerPage !== this.itemsPerPage) { this.itemsPerPage = itemsPerPage; changed = true; }
      // the order goes to the server (it was never sent: only the arrow changed)
      const sort = sortBy && sortBy.length ? { key: sortBy[0].key, order: sortBy[0].order } : null;
      if (JSON.stringify(sort) !== JSON.stringify(this.sortBy)) {
        this.sortBy = sort;
        if (this.page !== 1) this.page = 1;
        changed = true;
      }
      if (changed) this.loadSources();
    },
    onFilterDebounce() {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => { this.page = 1; this.loadSources(); }, 300);
    },
    async startMonitoring(item) {
      try {
        await apiClient.post(`/v2/platform-sources/${item.id}/start-monitoring`);
        item.is_monitoring = true;
        this.showSnackbar('Мониторинг запущен');
      } catch (e) {
        this.showSnackbar(e.response?.data?.detail || 'Ошибка запуска', 'error');
      }
    },
    async stopMonitoring(item) {
      try {
        await apiClient.post(`/v2/platform-sources/${item.id}/stop-monitoring`);
        item.is_monitoring = false;
        this.showSnackbar('Мониторинг остановлен');
      } catch (e) {
        this.showSnackbar(e.response?.data?.detail || 'Ошибка остановки', 'error');
      }
    },
    async refreshInfo(item) {
      try {
        await apiClient.post(`/v2/platform-sources/${item.id}/refresh-info`);
        // Optimistic flip: button starts spinning immediately. Backend
        // sets is_processing=True too, the polling below replaces this
        // local guess with the canonical state.
        item.is_processing = true;
        this.showSnackbar('Обновляем профиль');
        this.startProcessingPoll();
      } catch (e) {
        this.showSnackbar(e.response?.data?.detail || 'Ошибка', 'error');
      }
    },

    startProcessingPoll() {
      if (this.processingPollInterval) return;
      // Mirrors Telegram.vue: poll every 5s while at least one row is
      // is_processing=true. Refetch the page, replace items in place.
      this.processingPollInterval = setInterval(() => {
        this.checkProcessingStatus();
      }, 5000);
    },
    stopProcessingPoll() {
      if (this.processingPollInterval) {
        clearInterval(this.processingPollInterval);
        this.processingPollInterval = null;
      }
    },
    async checkProcessingStatus() {
      const stillProcessing = (this.sources || []).some(s => s.is_processing);
      if (!stillProcessing) {
        this.stopProcessingPoll();
        return;
      }
      try {
        const params = {
          platform_id: 13,
          offset: (this.page - 1) * this.itemsPerPage,
          limit: this.itemsPerPage,
        };
        if (this.filters.username) params.username = this.filters.username;
        if (this.filters.is_monitoring !== null && this.filters.is_monitoring !== undefined) {
          params.is_monitoring = this.filters.is_monitoring;
        }
        const resp = await apiClient.get('/v2/platform-sources', { params });
        const fresh = resp.data.items || [];
        // Detect transitions to notify the user.
        const finished = [];
        for (const old of this.sources) {
          if (old.is_processing) {
            const f = fresh.find(x => x.id === old.id);
            if (f && !f.is_processing) {
              finished.push(this.handleOf(f) || f.display_name || `#${f.id}`);
            }
          }
        }
        this.sources = fresh;
        this.total = resp.data.total || 0;
        if (finished.length) {
          this.showSnackbar(`Профиль обновлён: ${finished.join(', ')}`);
        }
        if (!fresh.some(s => s.is_processing)) {
          this.stopProcessingPoll();
        }
      } catch (e) {
        // Don't kill the poll on a single failed request — try again next tick.
        console.error('checkProcessingStatus error:', e);
      }
    },

    async loadReferenceData() {
      try {
        const [resCountries, resTopics] = await Promise.all([
          apiClient.get('/reference/geolocations/countries'),
          apiClient.get('/reference/topics'),
        ]);
        this.countries = (resCountries.data || []).map(it => ({ option: it.name, value: it.id }));
        this.sections = ((resTopics.data && resTopics.data.items) || []).map(it => ({ option: it.name, value: it.id }));
      } catch (e) {
        console.error('loadReferenceData error:', e);
      }
    },

    openEditModal(item) {
      this.editModal.item = { ...item };
      this.editForm.topic_id = item.topic_ids && item.topic_ids.length > 0 ? item.topic_ids[0] : null;
      this.editForm.geolocation_id = item.geolocation_id || null;
      this.editForm.status = item.status || 'active';
      this.editForm.tags = (item.tags || []).map(t => ({ id: t.id, name: t.name }));
      this.currentTagSearch = '';
      this.editModal.visible = true;
    },
    closeEditModal() {
      this.editModal.visible = false;
      this.editModal.item = null;
      this.editForm = { topic_id: null, geolocation_id: null, status: 'active', tags: [] };
      this.currentTagSearch = '';
    },
    async saveEdit() {
      if (!this.editModal.item) return;
      const tagsToSend = (this.editForm.tags || []).map(t => {
        if (t && typeof t === 'object' && t.id) return { id: t.id };
        if (t && typeof t === 'object' && t.name) return { name: t.name };
        if (typeof t === 'string') return { name: t };
        return null;
      }).filter(Boolean);
      this.editModal.saving = true;
      try {
        const payload = {
          status: this.editForm.status,
          tags: tagsToSend,
        };
        // Search-source has no meaningful Раздел/Страна — leave them
        // alone to avoid clearing whatever is currently set.
        if (!this.isSearchType(this.editModal.item)) {
          payload.topic_id = this.editForm.topic_id;
          payload.geolocation_id = this.editForm.geolocation_id;
        }
        await apiClient.put(`/v2/platform-sources/${this.editModal.item.id}`, payload);
        this.showSnackbar('Сохранено');
        this.closeEditModal();
        await this.loadSources();
      } catch (e) {
        this.showSnackbar(e.response?.data?.detail || 'Ошибка сохранения', 'error');
      } finally {
        this.editModal.saving = false;
      }
    },

    async searchTags(query) {
      this.currentTagSearch = query || '';
      if (!query) { this.tagSuggestions = []; return; }
      try {
        const resp = await apiClient.get('/tags', { params: { q: query } });
        this.tagSuggestions = (resp.data || []).map(t => ({ id: t.id, name: t.name }));
      } catch {
        this.tagSuggestions = [];
      }
    },
    handleTagEnter(event) {
      const name = (this.currentTagSearch || '').trim();
      if (!name) return;
      event.preventDefault();
      const exists = (this.editForm.tags || []).some(t =>
        (t.name || t || '').toLowerCase() === name.toLowerCase(),
      );
      if (!exists) this.editForm.tags.push({ name });
      this.currentTagSearch = '';
      this.$nextTick(() => {
        if (this.$refs.tagAutocomplete) {
          try { this.$refs.tagAutocomplete.search = ''; } catch (_e) { /* ignore */ }
        }
      });
    },
    onTagSelected() {
      this.currentTagSearch = '';
      this.$nextTick(() => {
        if (this.$refs.tagAutocomplete) {
          try { this.$refs.tagAutocomplete.search = ''; this.$refs.tagAutocomplete.focus(); } catch (_e) { /* ignore */ }
        }
      });
    },
    getTagName(item) {
      if (item && item.raw) return item.raw.name || item.raw;
      return (item && item.name) || item || '';
    },

    confirmDelete(item) {
      this.deleteModal.item = item;
      this.deleteModal.visible = true;
    },
    async doDelete() {
      if (!this.deleteModal.item) return;
      this.deleteModal.deleting = true;
      try {
        await apiClient.delete(`/v2/platform-sources/${this.deleteModal.item.id}`);
        this.showSnackbar('Источник удалён');
        this.deleteModal.visible = false;
        this.deleteModal.item = null;
        await this.loadSources();
      } catch (e) {
        this.showSnackbar(e.response?.data?.detail || 'Ошибка удаления', 'error');
      } finally {
        this.deleteModal.deleting = false;
      }
    },
    handleOf(item) {
      // The v2 endpoint fills telegram_username from source_identifiers
      // (or extended_attributes.screen_name as fallback for non-TG).
      return item.telegram_username || '';
    },
    isSearchType(item) {
      return (item || {}).resource_type === 'search_query';
    },
    truncate(text) {
      if (!text) return '';
      return text.length > 100 ? text.substring(0, 100) + '...' : text;
    },
    formatNumber(n) {
      if (n == null) return '';
      return Number(n).toLocaleString('ru-RU');
    },
    formatDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d.getTime()) || d.getFullYear() === 1970) return '';
      const pad = (x) => String(x).padStart(2, '0');
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    statusColor(s) {
      return { active: 'success', pending: 'grey', deleted: 'error', blocked: 'warning' }[s] || 'grey';
    },
    statusLabel(s) {
      return { active: 'Активен', pending: 'Ожидание', deleted: 'Удалён', blocked: 'Заблокирован' }[s] || s;
    },
    showSnackbar(text, color = 'success') {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>

<style>
.v-data-table th {
  font-weight: bold !important;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
